import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { IMAGES, CONTACT } from "@/assets";
import { MaskedLine } from "@/components/Reveal";

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
            <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
                <motion.img
                    src={IMAGES.water}
                    alt="Crystal clear swimming pool water"
                    data-testid="hero-background-image"
                    className="w-full h-full object-cover"
                    initial={{ scale: 1.2, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
                />
            </motion.div>
            <div className="absolute inset-0 bg-[#03070A]/55" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-[#03070A]/80 [mask-image:linear-gradient(to_top,black,transparent)]" />

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-24 md:pb-28"
            >
                <div className="overflow-hidden mb-6">
                    <motion.p
                        data-testid="hero-overline"
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#00E5FF]"
                    >
                        <MapPin className="w-3.5 h-3.5" />
                        {CONTACT.area} — Premium Pool Care
                    </motion.p>
                </div>

                <h1 className="font-display tracking-tighter leading-[0.95] text-[#F4F4F0]">
                    <MaskedLine delay={0.7} className="text-[13vw] md:text-[9vw] lg:text-[8vw]">
                        TJE POOL
                    </MaskedLine>
                    <MaskedLine delay={0.9} className="text-[13vw] md:text-[9vw] lg:text-[8vw] italic text-[#00E5FF]">
                        MAINTENANCE
                    </MaskedLine>
                </h1>

                <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                    <div className="overflow-hidden max-w-md">
                        <motion.p
                            data-testid="hero-subcopy"
                            initial={{ y: "110%" }}
                            animate={{ y: "0%" }}
                            transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
                            className="text-sm md:text-base text-[#C8CFD6] leading-relaxed"
                        >
                            Weekly cleans, equipment care and green pool recovery —
                            so your water stays swim-ready all year round.
                        </motion.p>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
                        className="flex items-center gap-4"
                    >
                        <a
                            href="#contact"
                            data-testid="hero-cta-book"
                            className="rounded-full bg-[#00E5FF] text-[#03070A] px-7 py-3.5 text-sm font-semibold tracking-wide hover:bg-[#F4F4F0] transition-colors duration-300"
                        >
                            Book a clean
                        </a>
                        <a
                            href="#gallery"
                            data-testid="hero-cta-work"
                            className="rounded-full border border-white/25 text-[#F4F4F0] px-7 py-3.5 text-sm tracking-wide hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors duration-300"
                        >
                            See our work
                        </a>
                    </motion.div>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
            >
                <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                >
                    <ArrowDown className="w-4 h-4 text-[#8A949E]" />
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;
